import { CompanySwitcher } from "@/components/company-switcher";
import { Link, router, usePage } from "@inertiajs/react";
import {
  CalendarDays,
  Check,
  ChevronDown,
  ChevronsUpDown,
  ClipboardList,
  House,
  Inbox,
  LayoutDashboard,
  LogOut,
  MapPin,
  Package,
  Plus,
  Settings,
  Sparkles,
  Users,
  X,
} from "lucide-react";
import type { ComponentProps } from "react";
import { useTranslation } from "react-i18next";
import {
  locationDashboardPath,
  logoutPath,
  newLocationJobPath,
  userProfilePath,
} from "@/routes";
import {
  activeDailyItem,
  dailyNavigation,
  locationSwitchPath,
} from "@/lib/navigation";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useSidebar,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";

const icons = {
  home: House,
  schedule: CalendarDays,
  requests: Inbox,
  board: LayoutDashboard,
  jobs: ClipboardList,
  customers: Users,
  inventory: Package,
  services: Sparkles,
  settings: Settings,
};

export function AppSidebar(props: ComponentProps<typeof Sidebar>) {
  const {
    props: { auth, notification_parent, app_name: appName },
    url,
  } = usePage();
  const { t } = useTranslation();
  const { setOpenMobile, isMobile } = useSidebar();
  const location = auth.location;
  const current = activeDailyItem(auth, url, notification_parent?.section);
  const userName =
    [auth.user?.first_name, auth.user?.last_name].filter(Boolean).join(" ") ||
    auth.user?.email_address;
  const close = () => setOpenMobile(false);

  return (
    <Sidebar variant="sidebar" {...props}>
      <SidebarHeader className="gap-4 p-4">
        <div className="flex items-center justify-between gap-2">
          <Link
            className="flex min-h-11 items-center gap-2 text-base font-bold tracking-tight"
            href={
              location ? locationDashboardPath(location.id) : userProfilePath()
            }
            onClick={close}
          >
            <span className="flex size-7 items-center justify-center rounded-md bg-sidebar-primary text-sidebar-primary-foreground">
              <Sparkles className="size-4" aria-hidden="true" />
            </span>
            {appName}
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label={t("navigation.close")}
            onClick={close}
          >
            <X aria-hidden="true" />
          </Button>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label={t("navigation.switch_location")}
            render={
              <SidebarMenuButton className="h-auto min-h-16 border border-sidebar-border px-3 py-2" />
            }
          >
            <MapPin aria-hidden="true" />
            <span className="min-w-0 flex-1 text-left">
              <span className="block truncate font-semibold">
                {location?.name || t("common.locations")}
              </span>
              <span className="block truncate text-xs opacity-75">
                {auth.account?.name}
              </span>
            </span>
            <ChevronsUpDown className="size-4" aria-hidden="true" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="min-w-64"
            side={isMobile ? "bottom" : "right"}
          >
            {auth.locations.map((item) => (
              <DropdownMenuItem
                key={item.id}
                render={
                  <Link
                    href={locationSwitchPath(
                      url,
                      item.id,
                      auth.user?.role || "member",
                      notification_parent?.section,
                    )}
                    onClick={close}
                  />
                }
              >
                <MapPin aria-hidden="true" />
                <span className="flex-1">{item.name}</span>
                {item.id === location?.id && <Check aria-hidden="true" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <CompanySwitcher />
        {location && (
          <Button
            className="min-h-11 w-full justify-start bg-sidebar-primary text-sidebar-primary-foreground hover:bg-brand-action-hover"
            render={
              <Link href={newLocationJobPath(location.id)} onClick={close} />
            }
          >
            <Plus aria-hidden="true" />
            {t("navigation.new_job")}
          </Button>
        )}
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup className="px-3">
          <SidebarGroupContent>
            <nav aria-label={t("navigation.daily")}>
              <SidebarMenu>
                {dailyNavigation(auth).map((item) => {
                  const Icon = icons[item.key as keyof typeof icons];
                  const active = current === item.key;
                  return (
                    <SidebarMenuItem key={item.key}>
                      <SidebarMenuButton
                        className="min-h-11 px-3"
                        isActive={active}
                        tooltip={t(item.label)}
                        render={
                          <Link
                            href={item.href}
                            aria-current={active ? "page" : undefined}
                            onClick={close}
                          />
                        }
                      >
                        <Icon aria-hidden="true" />
                        <span>{t(item.label)}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </nav>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border p-3">
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label={t("navigation.user_menu")}
            render={
              <SidebarMenuButton
                className="h-auto min-h-14 px-3"
                isActive={url.startsWith("/user/")}
              />
            }
          >
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-sidebar-border font-semibold">
              {userName?.slice(0, 1).toUpperCase()}
            </span>
            <span className="min-w-0 flex-1 truncate text-left">
              {userName}
            </span>
            <ChevronDown className="size-4" aria-hidden="true" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="min-w-56"
            side={isMobile ? "top" : "right"}
          >
            <DropdownMenuItem
              render={<Link href={userProfilePath()} onClick={close} />}
            >
              <Settings aria-hidden="true" />
              {t("common.settings")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                close();
                router.delete(logoutPath());
              }}
            >
              <LogOut aria-hidden="true" />
              {t("common.sign_out")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
